import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import '../../../../../../stylesheets/custom_scrollbar.scss'
import { FileExplorerFileTableConfig } from '../services/DoxExplorerTableConfig'
import DoxExplorerFilesTableHeader from './doxExplorerFilesTableHeader'
import { useFileSearch } from '../../Shared/hooks/useFileSearchHook'
import DoxExplorerFilesTableRow from './doxExplorerFilesTableRow'
import DoxExplorerTableMessage from './doxExplorerTableMessage'
import { useFileDownload } from '../hooks/useFileDownloadHook'
import { DoxFileOperations, DoxFileOperationStatus } from '../services/doxExplorerConsts'
import DoxExplorerGenericErrorMessage from './doxExplorerGenericErrorMessage'
import { isRestrictedFile, isSignedFile, isNeededFile } from '../../Shared/resources/DoxFileTypes'
import { trackEvent, HeapEvents } from '../../../../../../services/util/AnalyticsUtil'


const DoxExplorerFilesTable = (props) => {
    let fileCounter = 1;
    const [operatingFile, setOperatingFile] = useState(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isRenaming, setIsRenaming] = useState(false)
    const [isRecovering, setIsRecovering] = useState(false)
    const [downloadResults, setDownloadResults] = useState({})
    const [sortColumn, sortDirection, updateSort, status, fileList, filteredFileList, loaded, deleteFile, renameFile, reloadFileList]
        = useFileSearch(props.ticketId, props.sortOverride, props.searchValue)

    const [loadingFiles, handleFileAction] = useFileDownload();
    const handleUpdateSort = (sortCol, sortDir) => {
        props.onSelectSort(sortCol)
        updateSort(sortCol, sortDir)
    }

    useEffect(() => {
        let isMounted = true; // Track if the component is mounted

        const performFileOperation = async () => {
            try {
                await props.fileOperationListener(fileList, loaded);
            } catch (error) {
                if (!isMounted) return; // Don't update state if the component is unmounted
                console.error("Error in file operation:", error);
            }
        };

        performFileOperation();

        return () => {
            isMounted = false; // Set to false when the component unmounts
        };
    }, [filteredFileList, loaded, props.fileOperationListener]);

    const handleFileOperation = (operation, documentId) => {
        var file = (filteredFileList || []).find(x => x.documentId === documentId);
        if (file) {
            if (operation === DoxFileOperations.DELETE || operation === DoxFileOperations.RENAME) {
                if (isRestrictedFile(file)) {
                    // second off, all the restricted files cannot be deleted or renamed
                    handleOperationResult(documentId, operation === DoxFileOperations.DELETE
                        ? DoxFileOperationStatus.FAIL_FILE_NEEDED
                        : DoxFileOperationStatus.FAIL_RENAME_SIGNED_FILE)
                } else if (isRestrictedFile(file) && (isSignedFile(file) || isNeededFile(file))) {
                    // first off, all the signed and needed files cannot be deleted or renamed
                    handleOperationResult(documentId, operation === DoxFileOperations.DELETE
                        ? DoxFileOperationStatus.FAIL_FILE_NEEDED
                        : DoxFileOperationStatus.FAIL_RENAME_SIGNED_FILE)
                } else if (!file.uploaderIsBlock) {
                    // secondly, a tax pro cannot delete or rename a client's files.
                    handleOperationResult(documentId, operation === DoxFileOperations.DELETE
                        ? DoxFileOperationStatus.FAIL_DELETE_CLIENT_FILE
                        : DoxFileOperationStatus.FAIL_RENAME_CLIENT_FILE)
                } else {
                    setOperatingFile(file)
                    if (operation === DoxFileOperations.DELETE) {
                        setIsDeleting(true);
                    } else {
                        setIsRenaming(true);
                    }
                }
            } else if (operation === DoxFileOperations.VIEW || operation === DoxFileOperations.DOWNLOAD) {
                setDownloadResults(Object.assign({}, downloadResults, { [documentId]: null }))
                props.onShowNotification('Dox.DocumentExplorer.Preparing')
                handleFileAction(operation, documentId, file.fileName, file.fileType, handleOperationResult)
            } else if (operation === DoxFileOperations.RECOVER) {
                setDownloadResults(Object.assign({}, downloadResults, { [documentId]: null }))
                props.onShowNotification('Dox.DocumentExplorer.Recovering')
                setIsRecovering(true)
                handleFileAction(operation, documentId, file.fileName, file.fileType, handleRecoverResult)
            }
            else {
                alert(`handling ${operation} on ${documentId}`)
            }
        }
    }

    const handleRecoverResult = (documentId, statusCode) => {
        reloadFileList()
        trackEvent(HeapEvents.RECOVER_FILE, documentId)
    }

    const handleOperationResult = (documentId, statusCode) => {
        setDownloadResults(Object.assign({}, downloadResults, { [documentId]: statusCode }))
    }

    const handleCancelFileOperation = () => {
        setIsDeleting(false)
        setIsRenaming(false);
        setOperatingFile(null)
    }

    useEffect(() => {
        if (isDeleting) {
            props.onStartDelete({
                documentId: operatingFile.documentId,
                fileName: operatingFile.fileName + operatingFile.fileType,
                onCancel: handleCancelFileOperation,
                onDeleteFile: handleDeleteFile
            })
        }
    }, [isDeleting])

    useEffect(() => {
        if (isRenaming) {
            props.onStartRename({
                documentId: operatingFile.documentId,
                fileList: filteredFileList,
                onCancel: handleCancelFileOperation,
                onRenameFile: handleRenameFile
            })
        }
    }, [isRenaming])

    useEffect(() => {
        if (isRecovering) {
            setIsRecovering(false)
            props.onFileRecover()
        }
    }, [isRecovering])

    const handleDeleteFile = async (documentId) => {
        var response = await deleteFile(documentId);
        if (response) {
            handleCancelFileOperation()
            return null
        } else {
            return 'fail'
        }
    }

    const handleRenameFile = async (documentId, newFileName) => {
        var response = await renameFile(documentId, newFileName, props.ticketId);
        if (response) {
            handleCancelFileOperation()
            return null
        } else {
            return 'fail'
        }
    }

    const selectAllFiles = (allFilesSelected) => {
        if (allFilesSelected) {
            filteredFileList.filter(x => x.documentStatus !== 'Deleted').map(f => { props.onFileIdSelected(f.documentId) });
        }
        else {
            filteredFileList.filter(x => x.documentStatus !== 'Deleted').map(f => { props.onFileIdRemoved(f.documentId) });
        }
    }

    return (
        <div className={classnames('doxe-files-table', { 'is-hidden': props.hide })}>
            <DoxExplorerFilesTableHeader tableConfig={FileExplorerFileTableConfig} sortColumn={sortColumn} selectAllFiles={selectAllFiles}
                sortDirection={sortDirection} onSort={handleUpdateSort} bulkDownloadEligibility={props.bulkDownloadEligibility}
                fileIds={props.fileIds} fileList={fileList} />
            <div className='doxe-scroll-parent custom-scroll'>
                <div>
                    {!loaded &&
                        <DoxExplorerFilesTableRow layoutType={props.layoutType} isDownloading={false} onFileOperation={handleFileOperation} />
                    }
                    {loaded && filteredFileList.map(f =>
                        <DoxExplorerFilesTableRow key={`doxe-fr-${f.documentId}`} file={f} targetName={props.targetName}
                            layoutType={props.layoutType} sortColumn={sortColumn} isDownloading={loadingFiles.includes(f.documentId)}
                            onFileOperation={handleFileOperation} sortDirection={sortDirection} errorStatus={downloadResults[f.documentId]} isSoftDeleted={f.documentStatus === 'Deleted'}
                            isRecovering={isRecovering} fileCounter={fileCounter++} onFileIdSelected={props.onFileIdSelected} onFileIdRemoved={props.onFileIdRemoved}
                            bulkDownloadEligibility={props.bulkDownloadEligibility} fileIds={props.fileIds} />
                    )}
                    {loaded && !filteredFileList.length && status && !props.searchValue &&
                        <DoxExplorerTableMessage labelKeys={['Dox.DocumentExplorer.NoDocs']} />
                    }
                    {loaded && !filteredFileList.length && status && props.searchValue &&
                        <DoxExplorerTableMessage labelKeys={['Dox.DocumentExplorer.NoSearch1', 'Dox.DocumentExplorer.NoSearch2']}
                            params={[{ SearchTerm: props.searchValue }, null]} />
                    }
                    {loaded && !filteredFileList.length && !status &&
                        <DoxExplorerGenericErrorMessage />
                    }
                </div>
            </div>
        </div>
    )
}

DoxExplorerFilesTable.whyDidYouRender = false

DoxExplorerFilesTable.propTypes = {
    layoutType: PropTypes.string.isRequired,
    ticketId: PropTypes.number,
    sortOverride: PropTypes.string,
    onSelectSort: PropTypes.func.isRequired,
    onShowNotification: PropTypes.func.isRequired,
    targetName: PropTypes.string,
    searchValue: PropTypes.string,
    onStartDelete: PropTypes.func.isRequired,
    onStartRename: PropTypes.func.isRequired,
    hide: PropTypes.bool.isRequired
}

export default DoxExplorerFilesTable;